import React, { useMemo, useEffect } from "react";
import { Grid, Typography, Box, IconButton } from "@mui/material";
import TableComponentV2 from "../../../../common/components/TableComponentV2";
import { usePerStoreActiveSKUListing, useStoreZeroSalesOverView, usePerStoreActiveSKUListingInnovation } from "../hooks";
import LoadingComponent from "../../../../common/components/LoadingComponent";
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';

export default function ZeroSalesOverViewTable({ filters, searchQuery, handleRadioChange, setOverviewTableIsEmpty, toggleState }) {

    const { zeroSalesData, isLoadingZeroSalesData } = useStoreZeroSalesOverView({
        params: { ...filters },
    });

    const { perStoreActiveListing, isLoadingPerStoreActiveListing } = usePerStoreActiveSKUListing({
        params: { ...filters },
    });

    const { perStoreActiveListingInnovation, isLoadingPerStoreActiveListingInnovation } = usePerStoreActiveSKUListingInnovation({
        params: { ...filters },
    });

    /**
     * STEP 1: Structure Active Listing (888 & 4k)
     */
    const structuredActiveListing = useMemo(() => {
        if (!Array.isArray(perStoreActiveListing)) return [];

        const grouped = {};

        perStoreActiveListing.forEach(item => {
            const {
                company_id,
                store_id,
                article_sub_category,
                article_code_count_distinct
            } = item;

            const key = `${company_id}_${store_id}`;

            if (!grouped[key]) {
                grouped[key] = {
                    company_id,
                    store_id,
                    top888_active: 0,
                    top4k_active: 0
                };
            }

            if (article_sub_category === "888") {
                grouped[key].top888_active += article_code_count_distinct;
            } else if (article_sub_category === "4k") {
                grouped[key].top4k_active += article_code_count_distinct;
            }
        });

        return Object.values(grouped);
    }, [perStoreActiveListing]);


    /**
     * STEP 2: Create Innovation Lookup
     */
    const innovationLookup = useMemo(() => {
        if (!Array.isArray(perStoreActiveListingInnovation)) return {};

        const lookup = {};

        perStoreActiveListingInnovation.forEach(item => {
            const { store_id, article_type, article_number_count_distinct } = item;

            // Only include specific article types
            if (!['ZTRA', 'ZTRS'].includes(article_type)) return;

            // Sum counts if both types exist for the same store
            if (!lookup[store_id]) {
                lookup[store_id] = 0;
            }

            lookup[store_id] += article_number_count_distinct || 0;
        });

        return lookup;
    }, [perStoreActiveListingInnovation]);

    /**
     * STEP 3: Merge Everything
     */
    const mergedZeroSalesData = useMemo(() => {
        if (!Array.isArray(zeroSalesData)) return zeroSalesData;

        // Create lookup for active listing
        const activeLookup = {};
        structuredActiveListing.forEach(item => {
            activeLookup[item.store_id] = item;
        });

        return zeroSalesData.map(row => {
            const storeId = row.store_id;

            const active = activeLookup[storeId] || {};
            const active_innovation = innovationLookup[storeId] || 0;

            const top888_active = active.top888_active || 0;
            const top4k_active = active.top4k_active || 0;

            const innovation_count = row.innovation_count || 0
            const top_8k_count = row.top_8k_count || 0;
            const top_4k_count = row.top_4k_count || 0;

            let percent_zero_sales_888 = null;
            let percent_zero_sales_4k = null;
            let percent_zero_sales_innovation = null;

            if (top888_active > 0) {
                percent_zero_sales_888 = (top_8k_count / top888_active) * 100;
            }

            if (top4k_active > 0) {
                percent_zero_sales_4k = (top_4k_count / top4k_active) * 100;
            }

            if (active_innovation > 0) {
                percent_zero_sales_innovation = (innovation_count / active_innovation) * 100;
            }

            return {
                ...row,
                top888_active,
                top4k_active,
                active_innovation,
                percent_zero_sales_888,
                percent_zero_sales_4k,
                percent_zero_sales_innovation
            };
        });

    }, [zeroSalesData, structuredActiveListing, innovationLookup, filters.article_type]);


    useEffect(() => {
        setOverviewTableIsEmpty(!zeroSalesData || zeroSalesData?.length === 0);
    }, [zeroSalesData, setOverviewTableIsEmpty]);

    const filteredData = useMemo(() => {
        if (!searchQuery) return mergedZeroSalesData;
        return mergedZeroSalesData?.filter(item =>
            Object.values(item).some(value =>
                value?.toString().toLowerCase().includes(searchQuery?.toLowerCase())
            )
        );
    }, [mergedZeroSalesData, searchQuery]);

    const store_zero_sales_columns = useMemo(
        () => [
            {
                id: 'store_name',
                header: 'Store Name',
                accessorKey: "store_name",
                cell: (row) => {
                    const storeId = row?.row?.original?.store_id;
                    const storeName = row?.row?.original?.store_name;
                    return (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mx: 1 }}>
                            <IconButton
                                size="small"
                                onClick={() => handleRadioChange(storeId, storeName)}
                            >
                                {filters?.store_id === storeId ? (
                                    <RadioButtonCheckedIcon sx={{ color: "#1976D2" }} />
                                ) : (
                                    <RadioButtonUncheckedIcon sx={{ color: "#1976D2" }} />
                                )}
                            </IconButton>
                            <Typography>{storeName}</Typography>
                        </Box>
                    );
                },
                customStyle: { width: '23rem' }
            },
            {
                header: 'Top 888',
                id: 'top_888',
                columns: [
                    {
                        header: 'SKU Count',
                        accessorKey: 'top_8k_count',
                        cell: (row) => (
                            <Box sx={{ width: { sm: '20%' }, marginX: 'auto' }}>
                                <Typography>{row?.getValue()}</Typography>
                            </Box>
                        ),
                        customStyle: {
                            width: '10rem',
                            textAlign: "left",
                            verticalAlign: "middle",
                        },

                    },
                    {
                        header: 'SKU Active Total',
                        accessorKey: 'top888_active',
                        cell: (row) => (
                            <Box sx={{ width: { sm: '20%' }, marginX: 'auto' }}>
                                <Typography>{row?.getValue()}</Typography>
                            </Box>
                        ),
                        customStyle: {
                            width: '10rem',
                            textAlign: "left",
                            verticalAlign: "middle",
                        },
                    },
                    {
                        id: 'percent_zero_sales_888',
                        header: '% Zero Sales',
                        accessorKey: 'percent_zero_sales_888',
                        cell: (row) => {
                            const value = row?.getValue();
                            const formatted = value !== null && value !== undefined ? value.toLocaleString(undefined, { maximumFractionDigits: 1 }) + '%' : '-';
                            return (
                                <Box sx={{ width: { sm: '30%' }, marginX: 'auto' }}>
                                    <Typography>{formatted}</Typography>
                                </Box>
                            );
                        },
                        customStyle: {
                            width: '10rem',
                            textAlign: "left",
                            verticalAlign: "middle",
                        },
                    }
                ]
            },
            {
                header: 'Top 4k',
                id: 'top_4k',
                columns: [
                    {
                        header: 'SKU Count',
                        accessorKey: 'top_4k_count',
                        cell: (row) => {
                            const value = row?.getValue();
                            const formatted = typeof value === 'number' ? value.toLocaleString() : value;
                            return (
                                <Box sx={{ width: { sm: '30%' }, marginX: 'auto' }}>
                                    <Typography>{formatted}</Typography>
                                </Box>
                            );
                        },
                        customStyle: {
                            width: '10rem',
                            textAlign: "left",
                            verticalAlign: "middle",
                        },

                    },
                    {
                        header: 'SKU Active Total',
                        accessorKey: 'top4k_active',
                        cell: (row) => {
                            const value = row?.getValue();
                            const formatted = typeof value === 'number' ? value.toLocaleString() : value;
                            return (
                                <Box sx={{ width: { sm: '30%' }, marginX: 'auto' }}>
                                    <Typography>{formatted}</Typography>
                                </Box>
                            );
                        },
                        customStyle: {
                            width: '10rem',
                            textAlign: "left",
                            verticalAlign: "middle",
                        },
                    },
                    {
                        id: 'percent_zero_sales_4k',
                        header: '% Zero Sales',
                        accessorKey: 'percent_zero_sales_4k',
                        cell: (row) => {
                            const value = row?.getValue();
                            const formatted = value !== null && value !== undefined ? value.toLocaleString(undefined, { maximumFractionDigits: 1 }) + '%' : '-';
                            return (
                                <Box sx={{ width: { sm: '30%' }, marginX: 'auto' }}>
                                    <Typography>{formatted}</Typography>
                                </Box>
                            );
                        },
                        customStyle: {
                            width: '10rem',
                            textAlign: "left",
                            verticalAlign: "middle",
                        },
                    }
                ]
            }
        ],
        [filters?.store_id, toggleState]
    );

    const innovation_product_column = useMemo(
        () => [
            {
                id: 'store_name',
                header: 'Store Name',
                accessorKey: "store_name",
                cell: (row) => {
                    const storeId = row?.row?.original?.store_id;
                    const storeName = row?.row?.original?.store_name;
                    return (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mx: 1 }}>
                            <IconButton
                                size="small"
                                onClick={() => handleRadioChange(storeId, storeName)}
                            >
                                {filters?.store_id === storeId ? (
                                    <RadioButtonCheckedIcon sx={{ color: "#1976D2" }} />
                                ) : (
                                    <RadioButtonUncheckedIcon sx={{ color: "#1976D2" }} />
                                )}
                            </IconButton>
                            <Typography>{storeName}</Typography>
                        </Box>
                    );
                },
                customStyle: { width: '15rem' }
            },
            {
                header: 'Innovation Product',
                id: 'innovation_product',
                columns: [
                    {
                        header: 'SKU Count',
                        accessorKey: 'innovation_count',
                        cell: (row) => (
                            <Box sx={{ width: { sm: '10%' }, marginX: 'auto' }}>
                                <Typography>{row?.getValue()}</Typography>
                            </Box>
                        ),
                        customStyle: {
                            width: '10rem',
                            textAlign: "left",
                            verticalAlign: "middle",
                        },
                    },
                    {
                        header: 'SKU Active Total',
                        accessorKey: 'active_innovation',
                        cell: (row) => {
                            const value = row?.getValue();
                            const formatted = typeof value === 'number' ? value.toLocaleString() : value;
                            return (
                                <Box sx={{ width: { sm: '20%' }, marginX: 'auto' }}>
                                    <Typography>{formatted}</Typography>
                                </Box>
                            );
                        },
                        customStyle: {
                            width: '10rem',
                            textAlign: "left",
                            verticalAlign: "middle",
                        },

                    },
                    {
                        id: 'percent_zero_sales_888',
                        header: '% Zero Sales',
                        accessorKey: 'percent_zero_sales_innovation',
                        cell: (row) => {
                            const value = row?.getValue();
                            const formatted = value !== null && value !== undefined ? value.toLocaleString(undefined, { maximumFractionDigits: 1 }) + '%' : '-';
                            return (
                                <Box sx={{ width: { sm: '20%' }, marginX: 'auto' }}>
                                    <Typography>{formatted}</Typography>
                                </Box>
                            );
                        },
                        customStyle: {
                            width: '10rem',
                            textAlign: "left",
                            verticalAlign: "middle",
                        },
                    }
                ]
            }
        ],
        [filters?.store_id, toggleState]
    );

    let overview_column = store_zero_sales_columns;
    if (toggleState === 'innovation_product') {
        overview_column = innovation_product_column
    } else {
        overview_column = store_zero_sales_columns;
    }


    if (isLoadingZeroSalesData || isLoadingPerStoreActiveListing || isLoadingPerStoreActiveListingInnovation) {
        return <LoadingComponent />;
    }

    if (!zeroSalesData || zeroSalesData?.length === 0) {
        return (
            <Box flexGrow={1} xs={12} sx={{ backgroundColor: "#ffffff", borderRadius: "0 0 .5rem .5rem" }} >
                <Grid container justifyContent="center" alignItems="center" py={6}>
                    <Typography
                        sx={{ fontSize: "1.5rem", color: "black", textAlign: "center" }}
                    >
                        There’s no available data.
                    </Typography>
                </Grid>
            </Box>
        );
    }

    return (
        <Grid container xs={12} >
            <TableComponentV2
                columns={overview_column}
                data={filteredData || []}
                tableId={'storeZeroSalesTable'}
                isCompact={true}
                customTableHeaderStyle={{
                    backgroundColor: '#caddeb',
                }}
                customTableContainerStyle={{
                    borderRadius: "0",
                    boxShadow: 'none',
                }}
                hasPagination={true}
                initialSort={{ id: 'percent_zero_sales_888', desc: true }}
                isInventoryTable={true}
            />
        </Grid>
    );
}